<!-- ==================== COMPONENT TEMPLATE ==================== -->
<div class="outer-div" [ngClass]="mode" style="background-color:white;">

  <!-- DIALOG HEADER -->
  <div class="meddra-browser" style="margin:0">
    
    <!-- HEADER AND TOOLS SECTION -->
    <div class="label-bar">
      
      <!-- HEADER TITLE -->
      <label
        i18n="@@spmsUserReg"
        class="textColour header-label-text">
        MedDRA Browser
      </label>

      <!-- VERSION BANNER -->
      <div
        class="version-banner"
        [class.latest]="isLatestVersion"
        [class.older]="!isLatestVersion"
      >
        <i
          [class]="isLatestVersion ? 'pi pi-check-circle' : 'pi pi-info-circle'"
        ></i>
        v{{ selectedMeddraVersion?.label }} •
        {{ isLatestVersion ? "Active" : "View Only" }}
      </div>
      <span class="example-spacer"></span>

      <!-- RIGHT SIDE (TOOLS AND SELECTIONS) -->
      <div class="tools" style="display: flex; gap: 0.5rem;">
        
        <!-- MedDRA Version Select -->
        <mat-form-field appearance="outline">
          <mat-label>MedDRA Version</mat-label>
          <mat-select
            [(value)]="selectedMeddraVersion"
            (selectionChange)="onMeddraVersionChange()"
          >
            <mat-option *ngFor="let version of meddraVersions" [value]="version">
              {{ version.label }}
            </mat-option>
          </mat-select>
        </mat-form-field>

        <!-- Language Select -->
        <mat-form-field appearance="outline">
          <mat-label>Language</mat-label>
          <mat-select 
            [(ngModel)]="selectedLanguage"
            (selectionChange)="onLanguageChange()"
          >
            <mat-option *ngFor="let lang of languageOptions" [value]="lang">
              {{ lang.name }}
            </mat-option>
          </mat-select>
        </mat-form-field>

      </div>
    </div>

    <!-- ==================== TABS NAVIGATION ==================== -->
    <div class="browser-tabs">
      <button
        class="tab-button"
        [class.active]="activeTab === 'terms'"
        (click)="onTabChange('terms')"
      >
        <i class="pi pi-sitemap"></i> Terms
      </button>
      
      <button
        class="tab-button"
        [class.active]="activeTab === 'smqs'"
        (click)="onTabChange('smqs')"
      >
        <i class="pi pi-list"></i> SMQ
      </button>
      
      <button
        class="tab-button"
        [class.active]="activeTab === 'cmqs'"
        (click)="onTabChange('cmqs')"
      >
        <i class="pi pi-tags"></i> CMQ (Basket)
      </button>
    </div>

    <!-- ==================== SPLIT LAYOUT ==================== -->
    <div class="layout">
      
      <!-- LEFT TREE (scrollable) -->
      <div class="left-panel" [class.disabled-selection]="!isLatestVersion">
        
        <!-- ==================== TERMS TAB CONTENT ==================== -->
        <div class="panel-header" *ngIf="activeTab === 'terms'">
          <span>Terms Hierarchy</span>
        </div>

        <div class="panel-body" *ngIf="activeTab === 'terms'">
          <div class="search-container">
            <mat-form-field appearance="outline" class="search-input">
              <mat-label>Search Terms</mat-label>
              <input
                matInput
                type="text"
                [(ngModel)]="searchQuery"
                [disabled]="!isLatestVersion"
                placeholder="Search terms..."
                (keyup.enter)="onSearchChange()"
              />
              <button mat-icon-button matSuffix (click)="onSearchChange()"
                matTooltip="Search terms" matTooltipPosition="above">
                <mat-icon>search</mat-icon>
              </button>
              <button mat-icon-button matSuffix *ngIf="searchQuery?.trim()" 
                (click)="clearSearch()"
                matTooltip="Clear search" matTooltipPosition="above"
                style="margin-right: -8px;">
                <mat-icon>close</mat-icon>
              </button>
            </mat-form-field>
          </div>
          
          <div *ngIf="meddraVersions.length === 0" class="loading-message">
            Loading MedDRA versions...
          </div>

          <div
            *ngIf="meddraVersions.length > 0 && treeData.length === 0"
            class="loading-message"
          >
            Loading SOCs...
          </div>

          <p-tree
            [value]="treeData"
            selectionMode="single"
            (onNodeSelect)="onNodeSelect($event)"
            (onNodeExpand)="onNodeExpand($event)"
            [loading]="false"
            class="meddra-tree"
            *ngIf="meddraVersions.length > 0 && treeData.length > 0"
          >
            <ng-template let-node pTemplate="default">
              <div class="tree-node-content" style="margin-left: 1rem;" [class.search-highlight-node]="node.styleClass">
                <span
                  class="level-badge"
                   [attr.data-level]="node.data?.level?.toLowerCase()"
                >
                  {{ node.data?.level }}:
                </span>
                <span [innerHTML]="node.label"></span>
              </div>
            </ng-template>
          </p-tree>

          <div
            *ngIf="meddraVersions.length > 0 && treeData.length === 0"
            class="empty-state"
          >
            <i class="pi pi-info-circle"></i>
            <p>No SOCs found for selected version</p>
          </div>
        </div>

        <!-- ==================== SMQ TAB CONTENT ==================== -->
        <div class="panel-header" *ngIf="activeTab === 'smqs'">
          <span>SMQ Hierarchy</span>
          <small class="record-count">
            {{ filteredSMQList.length }} SMQ(s)
          </small>
        </div>

        <div class="panel-body" *ngIf="activeTab === 'smqs'">
          <div class="search-container">
            <mat-form-field appearance="outline" class="search-input">
              <mat-label>Search SMQs</mat-label>
              <input matInput type="text" [(ngModel)]="smqSearchQuery"
                (keyup.enter)="onSMQSearch()" (input)="onSMQSearchInputChange()"
                placeholder="Search PT name..."/>
              <button mat-icon-button matSuffix (click)="onSMQSearch()"
                matTooltip="Search PT names" matTooltipPosition="above">
                <mat-icon>search</mat-icon>
              </button>
              <button mat-icon-button matSuffix *ngIf="smqSearchQuery?.trim()" 
                (click)="clearSMQSearch()"
                matTooltip="Clear search" matTooltipPosition="above"
                style="margin-right: -8px;">
                <mat-icon>close</mat-icon>
              </button>
            </mat-form-field>
          </div>
          
          <div class="ultra-compact-legend" *ngIf="!loadingSMQs && smqTreeData.length > 0">
            <span class="legend-label">SMQ Types:</span>
            <div class="legend-items">
              <span class="legend-item hierarchical">
                <i class="pi pi-sitemap"></i> Hierarchical
              </span>
              <span class="legend-item non-hierarchical">
                <i class="pi pi-file"></i> Non-Hierarchical
              </span>
            </div>
          </div>
          
          <div *ngIf="loadingSMQs" class="loading-message">
            Loading SMQs...
          </div>

          <p-tree
            [value]="smqTreeData"
            selectionMode="single"
            (onNodeExpand)="onSMQNodeExpand($event)"
            (onNodeSelect)="onSMQNodeSelect($event)"
            [loading]="loadingSMQs"
            class="meddra-tree smq-tree"
            *ngIf="!loadingSMQs && smqTreeData.length > 0"
          >
            <ng-template let-node pTemplate="default">
              <div class="smq-tree-node" style="margin-left: 1rem;">
                <i 
                  [class]="getSMQNodeIcon(node)" 
                  [ngClass]="{
                    'hierarchical-icon': node.data?.has_hierarchy === 'true',
                    'leaf-icon': node.data?.has_hierarchy !== 'true'
                  }"
                ></i>
                
                <div class="smq-node-content">
                  <span class="smq-name">{{ node.label }}</span>
                  <span class="smq-code">({{ node.data?.code }})</span>
                  
                  <span class="smq-scope" *ngIf="node.data?.scope" 
                    [ngClass]="node.data?.scope?.toLowerCase()">
                    {{ node.data?.scope }}
                  </span>
                </div>
                
                <i *ngIf="node.loading" class="pi pi-spinner pi-spin loading-icon"></i>
              </div>
            </ng-template>
          </p-tree>

          <div
            *ngIf="!loadingSMQs && smqTreeData.length === 0"
            class="empty-state"
          >
            <i class="pi pi-info-circle"></i>
            <p>No SMQs found</p>
          </div>
        </div>

        <!-- ==================== CMQ TAB CONTENT ==================== -->
        <div class="panel-header" *ngIf="activeTab === 'cmqs'">
  <span>CMQ Basket List</span>
  <small class="record-count">
    {{ displayedRowCount }} CMQ(s)
  </small>
</div>

<div class="panel-body" *ngIf="activeTab === 'cmqs'">

  <!-- Search -->


  <!-- Loading -->
  <div *ngIf="loadingCMQs" class="loading-message">
    Loading CMQs...
  </div>

  <!-- AG GRID -->
  <div *ngIf="!loadingCMQs && meddraBaskets.length > 0"
       class="ag-theme-balham"
       style="width: 100%; height: 400px;">

    <ag-grid-angular
      style="width: 100%; height: 100%;"
      [rowData]="meddraBaskets"
      [columnDefs]="cmqColumnDefs"
      [defaultColDef]="defaultColDef"
      [rowHeight]="35"
      [headerHeight]="35"
      [enableBrowserTooltips]="true"
      [floatingFiltersHeight]="45"
      rowSelection="single"
      (gridReady)="onGridReady($event)"
      (rowClicked)="onCMQBasketChange($event.data)">
    </ag-grid-angular>

  </div>

  <!-- Empty -->
  <div *ngIf="!loadingCMQs && meddraBaskets.length === 0"
       class="empty-state">
    <i class="pi pi-info-circle"></i>
    <p>No CMQs found</p>
  </div>

</div>

      </div>

      <!-- ==================== RIGHT RESULTS PANEL ==================== -->
      <div class="right-panel">
        
        <!-- PANEL HEADER -->
        <div class="panel-header" style="display: flex; align-items: center; justify-content: space-between; padding: 8px 12px;">
          <span style="font-size: 16px; font-weight: 600"> Results </span>

          <div
            class="selected-hierarchy"
            *ngIf="selectedLevel && selectedName && activeTab === 'terms'"
            style="font-size: 14px; font-weight: 600; color: #0369a1; margin-left: auto; margin-right: 20px;"
          >
            {{ selectedLevel }}: {{ selectedName }}
          </div>

          <div
            class="selected-hierarchy"
            *ngIf="selectedMeddraBasket && activeTab === 'cmqs'"
            style="font-size: 14px; font-weight: 600; color: #0369a1; margin-left: auto; margin-right: 20px;"
          >
            <div style="display: flex; gap: 2rem; font-size: 14px; margin: 0.5rem 0;">
              <div>
                <strong style="color: black;">Basket Name:</strong>
                <span style="margin: 0 0 0 10px;">
                  {{ selectedMeddraBasket?.basketName || '-' }}
                </span>
              </div>

              <div>
                <strong style="color: black;">Basket State:</strong>
                <span style="margin: 0 0 0 10px;">
                  {{ selectedMeddraBasket?.state || '-' }}
                </span>
              </div>
              
              <div>
                <strong style="color: black;">Basket Version:</strong>
                <span style="margin: 0 0 0 10px;">
                  {{ selectedMeddraBasket?.basketVersion || '-' }}
                </span>
              </div>
            </div>
          </div>
          
          <div
            class="selected-hierarchy"
            *ngIf="activeTab === 'smqs' && selectedSMQ"
            style="font-size: 14px; font-weight: 600; color: #0369a1; margin-left: auto; margin-right: 20px;"
          >
            <div style="display: flex; gap: 2rem; font-size: 14px; margin: 0.5rem 0;">
              <div>
                <strong style="color: black;">SMQ Details:</strong>
                <span style="margin: 0 0 0 10px;">
                  {{ selectedSMQ?.name }}
                </span>
              </div>
            </div>
          </div>

          <small
            *ngIf="filteredTableData.length > 0 && activeTab === 'terms'"
            class="record-count"
            style="font-size: 13px; white-space: nowrap"
          >
            {{ filteredTableData.length }} record(s) found
          </small>
        </div>

        <!-- ==================== FILTER BAR ==================== -->
        <div class="filter-bar">
          <div class="filter-group" style="display: flex; align-items: center; gap: 20px">
            
            <div *ngIf="activeTab === 'terms'">
              <mat-form-field appearance="outline">
                <mat-label>Extraction Level</mat-label>
                <mat-select
                  [(ngModel)]="selectedExtractionLevel"
                  (selectionChange)="onExtractionLevelChange()"
                >
                  <mat-option *ngFor="let level of extractionLevelOptions" [value]="level.value">
                    {{ level.label }}
                  </mat-option>
                </mat-select>
              </mat-form-field>
            </div>

            <div *ngIf="activeTab === 'terms'">
              <mat-form-field appearance="outline" style="min-width: 200px;">
                <mat-label>Filter Options</mat-label>
                <mat-select
                  multiple
                  [(ngModel)]="selectedFilters"
                  (selectionChange)="onFilterSelectionChange()"
                >
                  <mat-option *ngFor="let filter of filterOptions" [value]="filter.value">
                    {{ filter.label }}
                  </mat-option>
                </mat-select>
              </mat-form-field>
            </div>
            
            <div *ngIf="activeTab === 'smqs' && selectedSMQ && isLatestVersion" 
              style="display: flex; align-items: center; gap: 10px;">
              
              <app-common-button
                *ngIf="activeTab === 'smqs'"
                label="Select SMQ"
                title="Select SMQ"
                [buttonId]="1111"
                [variant]="'primary'"
                style="margin-bottom: 1rem;"
                (clicked)="addSMQToSelection()"
              >
              </app-common-button>
            </div>
            
            <div *ngIf="activeTab === 'cmqs' && mode === 'select'" 
              style="display: flex; align-items: center; gap: 10px;">
              
              <button
                id="saveBtn"
                i18n="@@TMRunButton"
                type="button"
                *ngIf="activeTab === 'cmqs'"
                (click)="addCMQToSelection()"
                [style.background-color]="backgroundColorButton"
                class="btn btn-rounded-button z-depth-1 waves-effect waves-light text-capitalize"
                style="padding: 0.3rem 1.6rem; margin: 0 0 0.5rem"
              >
                Select CMQ
              </button>
            </div>
            
            <div *ngIf="activeTab === 'terms' || activeTab === 'smqs'">
              <mat-form-field appearance="outline">
                <mat-label>MedDRA Basket</mat-label>
                <mat-select
                  [(value)]="selectedMeddraBasket"
                  (selectionChange)="onMeddraBasketChange($event)"
                >
                  <mat-option *ngFor="let basket of meddraBaskets" [value]="basket">
                    {{ basket.basketName }}
                  </mat-option>
                  <mat-option value="CREATE_NEW">Create New Basket</mat-option>
                </mat-select>
              </mat-form-field>
            </div>

            <app-common-button
              *ngIf="(activeTab === 'terms' && selectedExtractionLevel != 'llt') || activeTab === 'smqs'"
              label="Add to Basket"
              title="Add to Basket"
              [buttonId]="1111"
              [variant]="'primary'"
              style="margin-bottom: 1rem;"
              (clicked)="AddtoBasket()"
            >
            </app-common-button>

            <app-common-button
              *ngIf="activeTab === 'cmqs' && isDraft()"
              label="Send to Review"
              title="Send to Review"
              [buttonId]="1111"
              [variant]="'primary'"
              style="margin-bottom: 1rem;"
              (clicked)="submitCMQForReview()"
            >
            </app-common-button>

            <app-common-button
              *ngIf="activeTab === 'cmqs' && isInReview()"
              label="Approve"
              title="Approve"
              [buttonId]="1111"
              [variant]="'primary'"
              style="margin-bottom: 1rem;"
              (clicked)="submitCMQForApproval()"
            >
            </app-common-button>

            <app-common-button
              *ngIf="activeTab === 'cmqs' && (isDraft() || isInReview())"
              label="Reject"
              title="Reject"
              [buttonId]="1111"
              [variant]="'primary'"
              style="margin-bottom: 1rem;"
              (clicked)="rejectCMQ()"
            >
            </app-common-button>

            <app-common-button
              *ngIf="activeTab === 'cmqs' && isApproved()"
              label="Retire"
              title="Retire"
              [buttonId]="1111"
              [variant]="'primary'"
              style="margin-bottom: 1rem;"
              (clicked)="retireCMQ()"
            >
            </app-common-button>
            
          </div>
        </div>

        <!-- ==================== PANEL BODY - AG Grid Container ==================== -->
        <div class="panel-body ag-grid-container">
          
          <!-- TERMS TAB CONTENT -->
          <div *ngIf="activeTab === 'terms'" class="terms-content">
            <ag-grid-angular
              class="ag-theme-balham terms-grid"
              style="height: 100%; width: 100%;"
              [gridOptions]="termsGridOptions"
              [rowData]="filteredTableData"
              [columnDefs]="columnDefs"
              [rowSelection]="isLatestVersion ? (mode === 'select' ? 'single' : 'multiple') : 'single'"
              [pagination]="true"
              [paginationPageSize]="10"
              (gridReady)="onGridReady($event)"
              (rowSelected)="onGridRowSelected($event)"
              (selectionChanged)="onGridSelectionChanged()"
              [animateRows]="true"
              [components]="components"
              [enableBrowserTooltips]="true"
            >
            </ag-grid-angular>
          </div>

          <!-- ==================== SMQ TAB CONTENT ==================== -->
          <div *ngIf="activeTab === 'smqs'" class="terms-content">
            
            <div *ngIf="selectedSMQ && selectedSMQ.has_hierarchy === 'true'" class="terms-content">
              <ag-grid-angular
               style="height: 100%; width: 100%;"
                class="ag-theme-balham results-grid"
                [gridOptions]="smqChildGridOptions"
                [rowData]="childSMQsData"
                [columnDefs]="smqChildColDefs"
                [pagination]="true"
                [paginationPageSize]="10"
                (gridReady)="onSMQChildGridReady($event)">
              </ag-grid-angular>
            </div>
            
            <div *ngIf="selectedSMQ && selectedSMQ.has_hierarchy === 'false'" class="terms-content">
              <ag-grid-angular
               style="height: 100%; width: 100%;"
                class="ag-theme-balham results-grid"
                [gridOptions]="smqTermsGridOptions"
                [rowData]="meddraTermsData"
                [columnDefs]="smqTermsColDefs"
                [pagination]="true"
                [paginationPageSize]="10"
                (gridReady)="onSMQTermsGridReady($event)">
              </ag-grid-angular>
            </div>
            
            <div *ngIf="!selectedSMQ" class="empty-state">
              <i class="pi pi-info-circle"></i>
              <p>Select an SMQ to view details</p>
            </div>
          </div>

          <!-- ==================== CMQ TAB CONTENT ==================== -->
          <div *ngIf="activeTab === 'cmqs'" class="terms-content">
            <ag-grid-angular
             style="height: 100%; width: 100%;"
              class="ag-theme-balham results-grid"
              [gridOptions]="cmqGridOptions"
              [rowData]="BasketData"
              [columnDefs]="cmqColDefs"
              [pagination]="true"
              [paginationPageSize]="10"
              (gridReady)="onCMQGridReady($event)"
              (rowSelected)="onCMQGridRowSelected($event)"
              (selectionChanged)="onCMQGridSelectionChanged()"
              [animateRows]="true"
              [components]="components"
              [enableBrowserTooltips]="true"
               [suppressBrowserResizeObserver]="true"
                [suppressPropertyNamesCheck]="true"
            >
            </ag-grid-angular>
          </div>

          <!-- ==================== SELECTION PANEL ==================== -->
          <div class="selection-panel" *ngIf="selectedItems.length > 0">
            <div class="selection-header">
              <h5>Selected Items ({{ selectedItems.length }})</h5>
              <button mat-button (click)="clearAllSelections()">
                Clear All
              </button>
            </div>
            
            <div class="selection-list" *ngIf="activeTab === 'terms'">
              <div
                *ngFor="let item of selectedItems; let i = index"
                class="selected-item"
              >
                <span class="item-name">{{selectedExtractionLevel | uppercase}} :</span>
                <span class="item-code">({{ item.code }})</span>
                <span class="item-level" style="color: black;">{{ item.name || item.term }}</span>
                <button class="remove-btn" (click)="removeSelection(i)">
                  <mat-icon>close</mat-icon>
                </button>
              </div>
            </div>

            <div class="selection-list" *ngIf="activeTab !== 'terms'">
              <div
                *ngFor="let item of selectedItems; let i = index"
                class="selected-item"
              >
                <span class="item-name">{{ item.name || item.term }}</span>
                <span class="item-code" *ngIf="item.code">({{ item.code }})</span>
                <span class="item-level">{{ item.level }}</span>
                <span class="item-type" *ngIf="item.type === 'SMQ'">(SMQ)</span>
                <span class="item-type" *ngIf="item.type === 'TERM'">(Term)</span>
                <span class="item-type" *ngIf="item.type === 'CMQ'">(CMQ)</span>
                <button class="remove-btn" (click)="removeSelection(i)">
                  <mat-icon>close</mat-icon>
                </button>
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  </div>
</div>

<!-- ==================== DIALOG FOOTER (Select Mode Only) ==================== -->
<ng-container *ngIf="mode === 'select'">
  <div class="dialog-footer">
    <app-common-button
      title="Cancel"
      label="Cancel"
      [variant]="'cancel'"
      [buttonId]="1111"
      (clicked)="cancel()"
    >
    </app-common-button>
    
    <app-common-button
      title="Submit"
      label="Submit"
      [variant]="'primary'"
      [buttonId]="1111"
      (clicked)="onSubmit()"
    >
    </app-common-button>
  </div>
</ng-container>